import { formatFileSize, getFileIcon } from "../../../utils/helperFunctions";
import { Trash2, CheckCircle2 } from "lucide-react";
import { Progress } from "@heroui/react";

export const UploadedFilesUI = ({
  file,
  handleDelete,
  handleSubmitButtonClick,
  handleDescriptionChange,
  description,
  isUploaded = false,
  isUploading = false,
  uploadProgress = 0,
  isEditMode = false,
  localOnlyMode = false,
}) => {
  const fileId =
    typeof file.id === "string" ? file.id : String(file.id || "file");

  return (
    <div key={fileId} className="border border-gray-300 rounded-md p-4">
      <div className="flex items-center space-x-3.5 w-full">
        <div className="text-[#207C9733] bg-[#207C9733] rounded-full p-2 self-start">
          {getFileIcon(file.type)}
        </div>
        <div className="flex flex-col gap-3 w-full">
          {/* file details */}
          <div className="flex items-center justify-between gap-x-4">
            <div className="flex flex-col gap-0.5 w-[95%]">
              <div className="flex items-center text-xs font-medium text-gray-800">
                {typeof file.name === "string"
                  ? file.name
                  : String(file.name || "Unknown file")}
              </div>
              <div className="text-xs text-gray-500">
                {formatFileSize(file.size || 0)}
              </div>
              {isUploading && (
                <div className="flex w-full gap-3 items-center mt-1">
                  <Progress
                    aria-label="file-upload-progress"
                    classNames={{
                      track: "bg-[#E9EAEB]",
                      indicator: "bg-[#207C97]",
                    }}
                    value={uploadProgress}
                    className="flex-1 h-2"
                  />
                  <span className="text-xs text-gray-800 font-medium min-w-[50px] text-right">
                    {uploadProgress}%
                  </span>
                </div>
              )}
              {isUploaded && !localOnlyMode && (
                <div className="flex items-center gap-2 mt-1">
                  <CheckCircle2 className="w-4 h-4 text-secondary" />
                  <span className="text-xs text-secondary font-medium">
                    Uploaded successfully
                  </span>
                </div>
              )}
            </div>
            {/* Delete button */}
            {file.completed && (
              <button
                onClick={() => handleDelete(fileId)}
                className="text-gray-500 hover:text-red-500"
                title="Delete file"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>

          {/* Show description input or read-only context */}
          {(localOnlyMode || !isUploaded) && (
            <>
              {(() => {
                const isExistingAsset = Boolean(file?.assetId);
                const showReadOnlyContext = isEditMode && isExistingAsset;
                const contextText =
                  typeof description === "string"
                    ? description
                    : String(description || "");

                if (showReadOnlyContext) {
                  return (
                    <p className="w-full p-2.5 text-sm text-gray-700 bg-gray-50 rounded border border-gray-200">
                      {contextText || "No context"}
                    </p>
                  );
                }
                return (
                  <input
                    type="text"
                    value={contextText}
                    onChange={(e) =>
                      handleDescriptionChange(fileId, e.target.value)
                    }
                    placeholder="Add context so we understand what this file is about and can use correctly later"
                    className={`w-full p-2.5 border-b-1.5 placeholder:text-gray-500 placeholder:text-[13px] placeholder:font-normal outline-none text-sm transition-colors border-gray-300 focus:border-secondary ${
                      isUploading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={isUploading}
                  />
                );
              })()}
              {/* In localOnlyMode, files are auto-added on select and context syncs on change - no Add button needed */}
              {handleSubmitButtonClick && !localOnlyMode && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmitButtonClick(file, description);
                  }}
                  disabled={isUploading}
                  className="px-[14px] py-[8px] gap-[8px] bg-secondary text-background font-medium rounded-lg shadow hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isUploading
                    ? "Uploading..."
                    : isUploaded && isEditMode
                      ? "Update"
                      : "Submit"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
